import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams, NavLink } from 'react-router-dom';
import { useApi } from 'model/Api';
import {
  Report,
  ReportWorkResource,
  reportContractTypeList,
  reportMemberStatusList,
} from 'model/Report';
import { WorkResource } from 'model/WorkResource';
import { mediaQurey, generalStyle } from 'helper/Style';
import { convertCosts, convertDate, convertTime, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import H2 from 'component/part/H2';
import ConfirmResultModal from 'component/part/ConfirmResultModal';

type ReportMachineryType = 'machinery' | 'att' | 'car';
type ReportWorkResourceType = Exclude<WorkResource['type'], ReportMachineryType>;

type ReportWorkResourceGroups = {
  [type in ReportWorkResourceType]: {
    group: number;
    list: (WorkResource & ReportWorkResource)[];
  }[];
};

const reportWorkResourceTypes: ReportWorkResourceType[] = ['person', 'tool', 'car_related', 'industrial_waste', 'other'];

const makeInitialReportWorkResourceGroups = () =>
  reportWorkResourceTypes.reduce((groups, type) => ({ ...groups, [type]: [] }), {}) as ReportWorkResourceGroups;

type ReportMachineGroups = {
  type: ReportMachineryType;
  selectedList: (WorkResource & ReportWorkResource)[];
}[];

const machineryLabel = {
  machinery: '重機',
  att: 'ATT',
  car: '車両',
};

const resourceUrl = process.env.REACT_APP_RESOURCE_ENDPOINT || '';

const style = {
  layout: {
    display: 'grid',
    width: 'fit-content',
    fontSize: '1.6rem',
    [mediaQurey('min', 'pc')]: {
      gridTemplateColumns: 'minmax(auto, 60rem) auto',
      gap: '4rem',
    },
    [mediaQurey('max', 'tablet')]: {
      gridTemplateRows: 'auto auto',
      gap: '2rem',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  dateArea: {
    display: 'flex',
    gap: '2rem 4rem',
    [mediaQurey('max', 'tablet')]: {
      flexWrap: 'wrap' as const,
    },
  },
  informationArea: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
  },
  informationLabel: {
    color: '#888888',
  },
  informationValue: {
  },
  memberOperation: {
    [mediaQurey('min', 'pc')]: {
      display: 'grid',
      gridTemplateColumns: '1fr auto auto auto auto',
      gap: '1rem 4rem',
    },
    [mediaQurey('max', 'tablet')]: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '2rem',
      paddingBottom: '2rem',
      borderBottom: 'solid 0.2rem #cccccc',
    },
    ['& input[type="radio"]']: {
      marginRight: '0.2rem',
    },
  },
  memberOperationPart: {
    [mediaQurey('min', 'pc')]: {
      display: 'contents',
    },
    [mediaQurey('max', 'tablet')]: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '2rem 4rem',
      '&:nth-of-type(1) > div:nth-of-type(1)': {
        flexGrow: '1',
      },
    },
  },
  option: {
    width: '100%',
    minHeight: '16rem',
    padding: '1rem',
  },
  workResourceArea: {
    ...generalStyle.formRow,
    flexWrap: 'wrap' as const,
  },
  workResourceItemArea: (valueType: WorkResource['valueType']) => ({
    count: {
      [mediaQurey('min', 'pc')]: {
        width: 'calc(25% - 1.5rem)',
      },
      [mediaQurey('max', 'tablet')]: {
        width: 'calc(33% - 1.4rem)',
      },
    },
    quantity: {
      [mediaQurey('min', 'pc')]: {
        width: 'calc(25% - 1.5rem)',
      },
      [mediaQurey('max', 'tablet')]: {
        width: 'calc(33% - 1.4rem)',
      },
    },
    volume: {
      [mediaQurey('min', 'pc')]: {
        width: 'calc(25% - 1.5rem)',
      },
      [mediaQurey('max', 'tablet')]: {
        width: 'calc(33% - 1.4rem)',
      },
    },
    text: {
      width: 'calc(50% - 1rem) !important',
    },
    free_text: {
      width: '100%',
    }
  }[valueType]),
  workResourceItem: {
    ...generalStyle.item(),
    display: 'flex',
    justifyContent: 'right',
    alignItems: 'center',
    gap: '0.5rem',
  },
  resourceArea: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
  },
  resourceItem: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr 10rem',
    gridTemplateRows: 'auto auto',
    gap: '0.5rem',
    alignItems: 'center',
    border: 'solid 0.1rem #cccccc',
    borderRadius: '0.5rem',
    padding: '1rem',
    ['& > input,& > div:nth-of-type(2)']: {
      gridColumn: '1 / 3',
      gridRow: '2',
    },
    ['& > img']: {
      gridColumn: '3',
      gridRow: '1 / 3',
      maxWidth: '10rem',
      maxHeight: '10rem',
    },
  },
  costArea: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    width: 'fit-content',
    ['& > div']: {
      display: 'contents',
      ['& > div']: {
        borderBottom: 'solid 0.1rem #cccccc',
        padding: '1rem 0',
      },
      ['& > div:nth-of-type(2n)']: {
        textAlign: 'right' as const,
        paddingLeft: '1rem',
      },
    },
  },
};

type WorkResourceGroupFormProps = {
  group: ReportWorkResourceGroups[ReportWorkResourceType];
};

const WorkResourceGroupForm = (props: WorkResourceGroupFormProps) =>
  <Fragment>
    {props.group.map((workResourceGroup, groupIndex) => (
      <div css={style.workResourceArea} key={`g.${groupIndex}`}>
        {workResourceGroup.list.map((workResource, index) => (
          <FormContent label={workResource.name} css={style.workResourceItemArea(workResource.valueType)} key={`g.${groupIndex}.${index}`}>
            {['count', 'volume'].includes(workResource.valueType) ? (
                <div css={style.workResourceItem}>
                  {(workResource.value || 0).toLocaleString()}
                  <span>{workResource.unitName}</span>
                </div>
            ) : (
              <div css={generalStyle.item()}>{workResource.text || 'ー'}</div>
            )}
          </FormContent>
        ))}
      </div>
    ))}
  </Fragment>;

const ReportShow: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string }>();
  const reportId = parseInt(urlParams.id || '');
  const [report, setReport] = useState<Report>();
  const [confirm, setConfirm] = useState(false);

  const onDeleteSubmit = async () => {
    if (!report) return false;
    const result = await api.report.delete(report.id);
    return result.ok;
  };

  const [workResourceGroups, setWorkResourceGroups] = useState<ReportWorkResourceGroups>(makeInitialReportWorkResourceGroups());
  const [machineryGroups, setMachineryGroups] = useState<ReportMachineGroups>([]);
  const contractType = reportContractTypeList.find(({ value }) => value === report?.contractType) || reportContractTypeList[0];
  const totalCosts = convertCosts(report?.costs);
  const defaultInDateTime = report?.startDatetime || 0;
  const defaultOutDateTime = defaultInDateTime ? defaultInDateTime + 32400 : 0;

  useEffect(() => {
    (async () => {
      const [reportResponse, workResourcesResponse] = await Promise.all([
        api.report.get(reportId, { with: ['ledger', 'leader', 'safetyOfficer', 'member', 'workProcess', 'resource', 'cost'] }),
        api.workResource.getsByOwn(),
      ]);
      const reportWorkResources = reportResponse?.workResources || [];
      const reportWorkResourceGroups = makeInitialReportWorkResourceGroups();
      const reportMachineryGroups: ReportMachineGroups = [
        { type: 'machinery', selectedList: [] },
        { type: 'att', selectedList: [] },
        { type: 'car', selectedList: [] },
      ];
      workResourcesResponse.forEach((workResource) => {
        const reportWorkResource = {
          ...workResource,
          ...(reportWorkResources.find(({ id }) => id === workResource.id) || { value: 0, text: '' }),
        };
        if (['machinery', 'att', 'car'].includes(workResource.type)) {
          const reportMachinery = {
            ...workResource,
            value: reportWorkResource.value || 0,
          };
          if (reportMachinery.value > 0) {
            const reportMachineryGroup = reportMachineryGroups.find((item) => item.type === workResource.type);
            reportMachineryGroup?.selectedList.push(reportMachinery);
          }
        } else {
          const workResourceType = workResource.type as ReportWorkResourceType;
          const workResourceGroup = reportWorkResourceGroups[workResourceType].find(({ group }) => group === workResource.group);
          if (workResourceGroup) {
            workResourceGroup.list.push(reportWorkResource);
          } else {
            reportWorkResourceGroups[workResourceType].push({ group: workResource.group, list: [reportWorkResource] });
            reportWorkResourceGroups[workResourceType].sort((A, B) => A.group - B.group);
          }
        }
      });
      reportResponse && setReport(reportResponse);
      setWorkResourceGroups(reportWorkResourceGroups);
      setMachineryGroups(reportMachineryGroups);
    })();
  }, [reportId]);

  return <Layout>
    <ConfirmResultModal
      display={confirm}
      onHide={() => setConfirm(false)}
      onSubmit={onDeleteSubmit}
      onOk={() => navigate('/report/list')}
      objectType='report'
      operationType='delete'
    />
    <BackLink link='/report/list' label='一覧'></BackLink>
    <div css={style.layout}>
      <div>
        <H2 css={style.header}>
          日報：{report?.ledger?.shortName || ''}
          {report?.fixed ? '' : '（下書き中）'}
        </H2>
        <div css={generalStyle.form}>
          <div css={style.dateArea}>
            <FormContent label='作業日'>
              <div css={generalStyle.item()}>{convertDate(report?.startDatetime || 0)}</div>
            </FormContent>
            <FormContent label='開始時間'>
              <div css={generalStyle.item()}>{convertTime(report?.startDatetime || 0)}</div>
            </FormContent>
            <FormContent label='終了時間'>
              <div css={generalStyle.item()}>{convertTime(report?.endDatetime || 0)}</div>
            </FormContent>
          </div>
          <FormContent label='現場種別'>
            <div css={generalStyle.item()}>{contractType.label}</div>
          </FormContent>
          <FormContent label='作業内容'>
            <div css={generalStyle.item()}>{report?.workProcess?.name || ''}</div>
          </FormContent>
          <FormContent label='作業内容詳細'>
            <div css={generalStyle.item()}>{report?.summary || ''}</div>
          </FormContent>
          <FormContent label='作業写真'>
            <div css={style.resourceArea}>
              {(report?.resources || []).map((resource, index) => (
                <div css={style.resourceItem} key={`resource.${index}`}>
                  {resource.thumbnailName ? (
                    <img src={`${resourceUrl}/C1/${report?.resourceCode || ''}/${resource.thumbnailName}`} />
                  ) : (
                    <a
                      href={`${resourceUrl}/C1/${report?.resourceCode || ''}/${resource.indexName}`}
                      download={resource.originalName}
                      target='_blank'
                      css={generalStyle.link}
                    >{resource.originalName}</a>
                  )}
                  {resource.detail?.summary && (
                    <div>{resource.detail.summary}</div>
                  )}
                </div>
              ))}
            </div>
          </FormContent>
          {(report?.memberUsers || []).map((user, userIndex) => (
            <div css={style.memberOperation} key={`user.${userIndex}`}>
              <div css={style.memberOperationPart} key={`user.${userIndex}`}>
                <FormContent label={`作業員氏名${userIndex + 1}`}>
                  <div css={generalStyle.item()}>{convertUserName(user)}</div>
                </FormContent>
                <FormContent label='勤怠ステータス'>
                  <div css={generalStyle.item()}>
                    {
                      ((memberStatus) => (
                        memberStatus?.label || ''
                      ))(reportMemberStatusList.find(({ value }) => value === user.status))
                    }
                  </div>
                </FormContent>
              </div>
              <div css={style.memberOperationPart}>
                <FormContent label='開始時間'>
                  <div css={generalStyle.item()}>
                    {user.status === 1 ? (convertTime(user.inDatetime || defaultInDateTime)) : 'ー'}
                  </div>
                </FormContent>
                <FormContent label='終了時間'>
                  <div css={generalStyle.item()}>
                    {user.status === 1 ? (convertTime(user.outDatetime || defaultOutDateTime)) : 'ー'}
                  </div>
                </FormContent>
                {user.overTimeCost ? (
                  <FormContent label='残業手当'>
                    <div css={generalStyle.item()}>&yen;{user.overTimeCost.toLocaleString()}</div>
                  </FormContent>
                ) : (
                  <div />
                )}
              </div>
            </div>
          ))}
          {(report?.memberPartners || []).map((partner, partnerIndex) => (
            <div css={style.memberOperation} key={`partner.${partnerIndex}`}>
              <div css={style.memberOperationPart}>
                <FormContent label={`業者名${partnerIndex + 1}`}>
                  <div>{partner.name || ''}</div>
                </FormContent>
                <FormContent label='人数'>
                  <div css={generalStyle.item({ align: 'right' })}>{partner.count}人</div>
                </FormContent>
              </div>
              <div css={style.memberOperationPart}>
                <FormContent label='開始時間'>
                  <div css={generalStyle.item()}>
                    {partner.status === 1 ? (convertTime(partner.inDatetime || defaultInDateTime)) : 'ー'}
                  </div>
                </FormContent>
                <FormContent label='終了時間'>
                  <div css={generalStyle.item()}>
                    {partner.status === 1 ? (convertTime(partner.outDatetime || defaultOutDateTime)) : 'ー'}
                  </div>
                </FormContent>
                {partner.overTimeCost ? (
                  <FormContent label='残業手当'>
                    <div css={generalStyle.item()}>&yen;{partner.overTimeCost.toLocaleString()}</div>
                  </FormContent>
                ) : (
                  <div />
                )}
              </div>
            </div>
          ))}
          <WorkResourceGroupForm group={workResourceGroups.person} />
          <WorkResourceGroupForm group={workResourceGroups.tool} />
          {machineryGroups.map(({ type, selectedList }) => (
            <div css={generalStyle.form} key={type}>
              {selectedList.map((machinery, index) => (
                <div css={style.memberOperation} key={`${type}.${index}`}>
                  <div css={style.memberOperationPart}>
                    <FormContent label={`${machineryLabel[type]}${index + 1}`}>
                      <div css={generalStyle.item}>{machinery.name}</div>
                    </FormContent>
                    <FormContent label='台数'>
                      <div css={generalStyle.item}>{machinery.value}</div>
                    </FormContent>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <WorkResourceGroupForm group={workResourceGroups.car_related} />
          <WorkResourceGroupForm group={workResourceGroups.industrial_waste} />
          <WorkResourceGroupForm group={workResourceGroups.other} />
          <FormContent label='経費'>
            <div css={style.costArea}>
              <div>
                <div>人工：</div>
                <div>&yen;{totalCosts.personal.toLocaleString()}</div>
              </div>
              <div>
                <div>機械：</div>
                <div>&yen;{totalCosts.macinery.toLocaleString()}</div>
              </div>
              <div>
                <div>産廃費用：</div>
                <div>&yen;{totalCosts.industrialWaste.toLocaleString()}</div>
              </div>
              <div>
                <div>現場経費：</div>
                <div>&yen;{totalCosts.other.toLocaleString()}</div>
              </div>
              <div>
                <div>小計：</div>
                <div>&yen;{totalCosts.total.toLocaleString()}</div>
              </div>
            </div>
          </FormContent>
          {report?.enableEdit && (
            <div css={generalStyle.formRow}>
              <NavLink to={`/report/${report?.dateCode || 0}/${report?.id || 0}/edit`} css={generalStyle.button()}>編集</NavLink>
              <button type='button' onClick={() => setConfirm(true)} css={generalStyle.button('warning')}>削除</button>
            </div>
          )}
        </div>
      </div>
      <div css={style.informationArea}>
        <div>
          <div css={style.informationLabel}>現場名略称</div>
          <div css={style.informationValue}>{report?.ledger?.shortName || ''}</div>
        </div>
        <div>
          <div css={style.informationLabel}>現場代理人</div>
          <div css={style.informationValue}>
            {(report?.leaders || []).map((member) => (
              <Fragment>
                {member.lastName || ''} {member.firstName || ''}<br />
              </Fragment>
            ))}
          </div>
        </div>
        <div>
          <div css={style.informationLabel}>上長</div>
          <div css={style.informationValue}>
            {(report?.safetyOfficers || []).map((member) => (
              <Fragment>
                {member.lastName || ''} {member.firstName || ''}<br />
              </Fragment>
            ))}
          </div>
        </div>
        <div>
          <div css={style.informationLabel}>元請け名</div>
          <div css={style.informationValue}>{report?.ledger?.orderingParty || 'ー'}</div>
        </div>
      </div>
    </div>
  </Layout>;
};

export default ReportShow;
